/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Select from "../../../components/SearchableSelect";
import { Close, Clear } from "@mui/icons-material";
import { submitRequestDialogStyles as styles } from "../styles";
import { useGetEnvs } from "../../../api/envs";
import { useSubmitDbSoftwareBuild } from "../../../api/softwareImages";
import DiscardChangesDialog from "./DiscardChangesDialog";
import useResizableDrawerWidth from "../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../components/DrawerSnapToggle";

const CONTEXT_FIELDS = [
  ["Engine", "engine"],
  ["Major Version Name", "majorVersionName"],
  ["Old Patch Version", "oldPatchVersion"],
  ["Old Patch Version Name", "oldPatchVersionName"],
];

const SCRIPT_FIELDS = [
  {
    key: "installation",
    label: "Installation Script",
    hint: "Runs on the patched instance. .sh or .sql",
  },
  {
    key: "validation",
    label: "Validation Script",
    hint: "Runs after install to verify the patch",
  },
];

const ACCEPTED_SCRIPT_EXTENSIONS = ["sh", "sql"];

const formatFileSize = (bytes) =>
  bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;

const SubmitDbPatchRequestDialog = ({
  open,
  resetDialog,
  callback,
  displayNotification,
  initPayload,
  availableClouds,
}) => {
  const [payload, setPayload] = useState({});
  const [scriptFiles, setScriptFiles] = useState({});
  const [scriptErrors, setScriptErrors] = useState({});
  const [baseline, setBaseline] = useState("");
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isWideEnough = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "submit-db-patch-request",
    defaultWidth: 700,
    maxWidth: 1000,
    enabled: isWideEnough,
  });

  const {
    postData: submitDbSoftwareBuild,
    response: submitDbSoftwareBuildResp,
    responseStatus: submitDbSoftwareBuildStatus,
    isLoading: isSubmitting,
  } = useSubmitDbSoftwareBuild();

  const { postData: getEnvs, response: getEnvsResp } = useGetEnvs();

  useEffect(() => {
    if (open) {
      getEnvs();
      const init = structuredClone(initPayload);
      setPayload(init);
      setBaseline(JSON.stringify(init));
    } else {
      setPayload({});
    }
    setScriptFiles({});
    setScriptErrors({});
    setConfirmDiscardOpen(false);
  }, [open, initPayload]);

  const isDirty = useMemo(
    () => open && JSON.stringify(payload) !== baseline,
    [open, payload, baseline]
  );

  // Backdrop click, Esc, × and Cancel all come through here; a dirty form
  // asks for confirmation before discarding.
  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      resetDialog();
    }
  };

  const handleDialogConfirm = () => {
    availableClouds?.forEach((cloud) => {
      if (payload?.tenant?.[cloud.toUpperCase()] === "No Tenant") {
        delete payload.tenant[cloud.toUpperCase()];
      }
    });
    submitDbSoftwareBuild(payload);
  };

  useEffect(() => {
    displayNotification(
      submitDbSoftwareBuildResp,
      submitDbSoftwareBuildStatus,
      callback
    );
    if (
      typeof submitDbSoftwareBuildStatus === "number" &&
      submitDbSoftwareBuildStatus >= 200 &&
      submitDbSoftwareBuildStatus < 300
    ) {
      resetDialog();
    }
  }, [submitDbSoftwareBuildResp, submitDbSoftwareBuildStatus]);

  const isValidPayload = useMemo(() => {
    if (
      !payload?.jiraId ||
      !payload?.patchVersion ||
      !payload?.patchVersionName ||
      !payload?.installationScript ||
      !payload?.validationScript
    ) {
      return false;
    }
    return true;
  }, [payload]);

  // Footer summary: what's still missing, phrased as in the mock, with the
  // two scripts collapsing to "both scripts" when neither is uploaded.
  const validationSummary = useMemo(() => {
    const missing = [];
    let count = 0;
    if (!payload?.patchVersion) {
      missing.push("new version");
      count += 1;
    }
    if (!payload?.patchVersionName) {
      missing.push("new name");
      count += 1;
    }
    if (!payload?.jiraId) {
      missing.push("Jira Id");
      count += 1;
    }
    const noInstall = !payload?.installationScript;
    const noValidation = !payload?.validationScript;
    if (noInstall && noValidation) {
      missing.push("both scripts");
      count += 2;
    } else if (noInstall) {
      missing.push("installation script");
      count += 1;
    } else if (noValidation) {
      missing.push("validation script");
      count += 1;
    }
    if (!count) return "";
    return `${count} required field${count > 1 ? "s" : ""} left: ${missing.join(
      ", "
    )}`;
  }, [payload]);

  const runningTenants = useMemo(
    () =>
      getEnvsResp?.response?.filter((tenant) => tenant?.status === "RUNNING") ||
      [],
    [getEnvsResp]
  );

  const handleScriptFile = async (scriptKey, file) => {
    if (!file) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_SCRIPT_EXTENSIONS.includes(extension)) {
      setScriptErrors((prev) => ({
        ...prev,
        [scriptKey]: `Unsupported file type .${extension} — use .sh or .sql`,
      }));
      return;
    }
    const fileContent = await file.text();
    setScriptFiles((prev) => ({
      ...prev,
      [scriptKey]: { name: file.name, size: file.size },
    }));
    setScriptErrors((prev) => ({ ...prev, [scriptKey]: "" }));
    setPayload((prev) => ({ ...prev, [scriptKey + "Script"]: fileContent }));
  };

  const handleClearScript = (scriptKey) => {
    setScriptFiles((prev) => ({ ...prev, [scriptKey]: undefined }));
    setScriptErrors((prev) => ({ ...prev, [scriptKey]: "" }));
    setPayload((prev) => ({ ...prev, [scriptKey + "Script"]: undefined }));
  };

  const handleSelectAllTenants = () => {
    const firstTenant = runningTenants[0]?.url;
    if (!firstTenant) return;
    setPayload((prev) => ({
      ...prev,
      tenant: {
        ...prev?.tenant,
        ...Object.fromEntries(
          availableClouds.map((cloud) => [cloud.toUpperCase(), firstTenant])
        ),
      },
    }));
  };

  const renderPairRow = (label, oldValue, newLabel, fieldProps) => (
    <Box sx={styles.pairRow}>
      <Box>
        <FormLabel sx={styles.fieldLabel}>{label}</FormLabel>
        <Box sx={styles.pairValueBox}>{oldValue || "—"}</Box>
      </Box>
      <Box sx={styles.pairArrow}>→</Box>
      <Box>
        <FormLabel required sx={styles.fieldLabel}>
          {newLabel}
        </FormLabel>
        <TextField
          fullWidth
          size="small"
          sx={styles.fieldInput}
          {...fieldProps}
        />
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      PaperProps={{
        sx: { ...styles.drawerPaper(drawerWidth), overflow: "visible" },
      }}
    >
      <DrawerResizeHandle
        {...resizeHandleProps}
        ariaLabel="Resize submit DB patch request drawer"
      />
      <DrawerSnapToggle
        enabled={isWideEnough}
        isWide={isDrawerWide}
        onClick={toggleDrawerWide}
        ariaLabel="Expand submit DB patch request drawer to 75% width"
        collapseLabel="Collapse submit DB patch request drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      <Box sx={styles.drawerHeader}>
        <Box>
          <Typography sx={styles.title}>Submit DB Patch Request</Typography>
          <Typography sx={styles.subtitle}>
            {`Patching ${
              initPayload?.oldPatchVersionName ||
              initPayload?.majorVersionName ||
              ""
            }`}
          </Typography>
        </Box>
        <IconButton sx={styles.closeButton} onClick={requestClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={styles.drawerBody}>
        <Box sx={styles.contextBlock}>
          {CONTEXT_FIELDS.map(([label, key]) => (
            <Box key={key}>
              <Typography sx={styles.contextLabel}>{label}</Typography>
              <Typography sx={styles.contextValue}>
                {initPayload?.[key] || "—"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={styles.sectionHeading}>New Version</Typography>
        {renderPairRow(
          "Old Patch Version",
          initPayload?.oldPatchVersion,
          "New Patch Version",
          {
            value: payload?.patchVersion || "",
            onChange: (e) => {
              setPayload((prev) => {
                const newPatchVersionName =
                  prev?.oldPatchVersionName?.replaceAll(
                    prev?.oldPatchVersion,
                    e.target.value
                  );

                const out = {
                  ...prev,
                  patchVersion: e.target.value,
                };
                if (newPatchVersionName !== prev?.oldPatchVersionName) {
                  out.patchVersionName = newPatchVersionName;
                }
                return out;
              });
            },
          }
        )}
        {renderPairRow(
          "Old Patch Version Name",
          initPayload?.oldPatchVersionName,
          "New Patch Version Name",
          {
            value: payload?.patchVersionName || "",
            onChange: (e) =>
              setPayload((prev) => ({
                ...prev,
                patchVersionName: e.target.value,
              })),
          }
        )}
        <Box sx={styles.halfRow}>
          <FormLabel required sx={styles.fieldLabel}>
            Jira Id
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            sx={styles.fieldInput}
            value={payload?.jiraId || ""}
            onChange={(e) =>
              setPayload((prev) => ({ ...prev, jiraId: e.target.value }))
            }
          />
        </Box>

        <Divider sx={styles.sectionDivider} />

        <Typography sx={styles.sectionHeading}>Scripts</Typography>
        <Box sx={styles.uploadGrid}>
          {SCRIPT_FIELDS.map(({ key, label, hint }) => {
            const file = scriptFiles[key];
            const error = scriptErrors[key];
            const inputId = `upload-${key}-script`;
            return (
              <Box key={key} sx={styles.uploadCard(Boolean(error))}>
                <FormLabel required sx={styles.fieldLabel}>
                  {label}
                </FormLabel>
                <Typography sx={styles.uploadHint}>{hint}</Typography>
                <input
                  accept=".sh,.sql"
                  type="file"
                  id={inputId}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleScriptFile(key, e.target.files[0]);
                    e.target.value = "";
                  }}
                />
                <Box sx={styles.uploadFileRow}>
                  {file && (
                    <>
                      <Typography sx={styles.uploadFileName}>
                        {file.name} ({formatFileSize(file.size)})
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleClearScript(key)}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  <label htmlFor={inputId}>
                    <Button
                      size="small"
                      variant="outlined"
                      component="span"
                      sx={styles.uploadButton}
                    >
                      {file ? "Replace" : "Choose file"}
                    </Button>
                  </label>
                </Box>
                {error && (
                  <Typography sx={styles.uploadError}>{error}</Typography>
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={styles.sectionDivider} />

        <Box sx={styles.sectionHeadRow}>
          <Typography sx={{ ...styles.sectionHeading, mb: 0 }}>
            Roll Out To Tenants
          </Typography>
          <Button
            size="small"
            sx={styles.selectAllButton}
            onClick={handleSelectAllTenants}
          >
            Select all
          </Button>
        </Box>
        <Box sx={styles.tenantGrid}>
          {availableClouds?.map((cloud) => (
            <Box key={cloud}>
              <FormLabel sx={styles.fieldLabel}>
                {cloud?.toUpperCase()}
              </FormLabel>
              <Select
                fullWidth
                size="small"
                displayEmpty
                sx={styles.tenantSelect}
                value={payload?.tenant?.[cloud.toUpperCase()] || "No Tenant"}
                renderValue={(value) =>
                  !value || value === "No Tenant" ? "None" : value
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    tenant: {
                      ...prev?.tenant,
                      [cloud.toUpperCase()]: e.target.value,
                    },
                  }));
                }}
              >
                <MenuItem value={"No Tenant"}>None</MenuItem>
                {runningTenants.map((tenant, i) => (
                  <MenuItem value={tenant?.url} key={i}>
                    {tenant?.url}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={styles.drawerFooter}>
        <Typography sx={styles.footerSummary(!isValidPayload)}>
          {isValidPayload
            ? "All required fields are filled."
            : validationSummary}
        </Typography>
        <Box sx={styles.actionButtons}>
          <Button
            variant="outlined"
            color="inherit"
            sx={styles.actionButton}
            onClick={requestClose}
          >
            Cancel
          </Button>
          <Tooltip title={isValidPayload ? "" : validationSummary}>
            <span>
              <Button
                variant="contained"
                sx={styles.actionButton}
                disabled={!isValidPayload || isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : null
                }
                onClick={handleDialogConfirm}
              >
                Confirm
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onKeepEditing={() => setConfirmDiscardOpen(false)}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          resetDialog();
        }}
      />
    </Drawer>
  );
};

export default SubmitDbPatchRequestDialog;

export const useSubmitDbPatchRequestDialog = (
  callback = () => {},
  displayNotification = () => {}
) => {
  const [open, setOpen] = useState(false);
  const [initPayload, setInitPayload] = useState({});
  const [availableClouds, setAvailableClouds] = useState([]);

  return {
    submitDbPatchRequestDialog: (
      <SubmitDbPatchRequestDialog
        open={open}
        resetDialog={() => setOpen(false)}
        callback={callback}
        displayNotification={displayNotification}
        initPayload={initPayload}
        availableClouds={availableClouds}
      />
    ),
    setOpenSubmitDbPatchRequestDialog: (initPayload, availableClouds) => {
      setOpen(true);
      setInitPayload(initPayload || {});
      setAvailableClouds(availableClouds || []);
    },
  };
};
